<template>
  <div data-cy="conflict-saved-check">
    <div class="mt-4 flex flex-wrap items-center gap-3 print:hidden">
      <LlSecondaryButton
        prepend-icon="mdi-arrow-left"
        data-cy="conflict-saved-back"
        @click="emit('back')"
      >
        Previous checks
      </LlSecondaryButton>
      <LlSecondaryButton
        v-if="record"
        class="ml-auto"
        data-cy="conflict-saved-print"
        @click="emit('print')"
      >
        Print
      </LlSecondaryButton>
    </div>

    <v-skeleton-loader
      v-if="recordStore.recordLoading"
      class="mt-4"
      type="heading, paragraph, list-item-three-line@3"
      data-cy="conflict-saved-loading"
    />

    <!-- ERR-010: distinct from an empty check, which would read as a clean one. -->
    <div
      v-else-if="recordStore.recordNotFound"
      class="mt-4 rounded border border-gray-300 bg-gray-50 px-3.5 py-3 text-xs leading-relaxed"
      data-cy="conflict-saved-not-found"
    >
      <span class="font-medium">This saved check could not be found.</span>
      It may belong to another firm, or the link may be wrong.
    </div>

    <div
      v-else-if="recordStore.recordError"
      class="mt-4 rounded border border-l-4 border-[rgba(var(--v-theme-error),0.3)] bg-[rgba(var(--v-theme-error),0.08)] px-3.5 py-3 text-xs leading-relaxed"
      data-cy="conflict-saved-error"
    >
      <span class="font-medium text-red-700"
        >The saved check could not be loaded.</span
      >
      Nothing is shown, so nothing here says what it found.
      <div class="mt-2.5">
        <LlSecondaryButton size="small" @click="emit('retry')">
          Try again
        </LlSecondaryButton>
      </div>
    </div>

    <template v-else-if="record">
      <ConflictPrintMasthead :sheet="sheet" />

      <!--
        The settled part of the page. It is framed as a filed document — tinted, ruled,
        headed with who ran it and when — so it reads as a record rather than as a
        form that happens to have no buttons. Only the notes below it may change.
      -->
      <section
        class="mt-4 rounded border border-gray-300 bg-gray-50 print:!mt-0 print:!border-0 print:bg-white"
        aria-labelledby="conflict-saved-heading"
        data-cy="conflict-saved-record"
      >
        <header
          class="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-gray-300 px-4 py-3 print:hidden"
        >
          <LlIcon icon="mdi-lock-outline" size="small" color="grey-darken-1" />
          <h2
            id="conflict-saved-heading"
            class="text-sm font-medium text-gray-800"
          >
            Saved check · results fixed
          </h2>
          <span class="text-xs text-gray-600" data-cy="conflict-saved-run">
            Run by {{ record.runByName }} on {{ runAt }}
          </span>
          <span class="text-xs text-gray-600">
            {{
              record.includedTimeEntries
                ? 'Time entries included'
                : 'Time entries not searched'
            }}
          </span>
        </header>

        <div class="px-4 pb-4 print:!p-0">
          <dl
            class="mt-3 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1 text-xs print:hidden"
          >
            <dt class="text-gray-500">Searched for</dt>
            <dd class="text-gray-800 [overflow-wrap:anywhere]">
              {{ record.queryText }}
            </dd>
            <dt class="text-gray-500">
              {{ record.parties.length }}
              {{ record.parties.length === 1 ? 'name' : 'names' }} checked
            </dt>
            <dd class="text-gray-800 [overflow-wrap:anywhere]">
              {{ record.parties.join(' · ') }}
            </dd>
          </dl>

          <div
            v-if="record.result.truncated"
            class="mt-4 rounded border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs"
          >
            Only the first {{ record.result.partiesChecked }} of
            {{ record.result.partiesFound }} names were checked.
          </div>

          <div
            v-if="!recordStore.allRows.length"
            class="mt-4 rounded border border-green-200 bg-green-50 px-3.5 py-3 text-xs leading-relaxed"
            data-cy="conflict-saved-clean"
          >
            <span class="font-medium text-green-800">No conflicts found.</span>
            Searched {{ CONFLICT_SCOPE_SENTENCE }}{{ scopeTail }} Invoice
            narratives are not searched.
          </div>

          <ConflictResultList
            v-else
            ref="resultList"
            :full-matches="recordStore.fullMatches"
            :partial-matches="recordStore.partialMatches"
            @open="emit('open', $event)"
          />

          <!-- UI-017: notes nobody here may change are part of the record itself. -->
          <div
            v-if="!record.notesEditable"
            class="mt-4 border-t border-gray-300 pt-3"
            data-cy="conflict-saved-notes-fixed"
          >
            <h3 class="text-sm font-medium text-gray-800">Notes</h3>
            <p
              v-if="record.notes"
              class="mt-1 whitespace-pre-line text-sm text-gray-800 [overflow-wrap:anywhere]"
            >
              {{ record.notes }}
            </p>
            <p v-else class="mt-1 text-sm italic text-gray-600">
              No notes were recorded.
            </p>
            <p
              class="mt-2 text-xs text-gray-500 print:hidden"
              data-cy="conflict-saved-notes-lock"
            >
              <span v-if="recordStore.notesRefusal" class="font-medium"
                >Your change was not saved.</span
              >
              {{ fixedNotesReason }}
            </p>
          </div>
        </div>
      </section>

      <!-- UI-012: the one part of a saved check its author may still change. -->
      <section
        v-if="record.notesEditable"
        class="mt-4 rounded border border-gray-200 bg-white px-4 py-3 print:!border-0 print:!px-0"
        aria-labelledby="conflict-saved-notes-heading"
        data-cy="conflict-saved-notes-open"
      >
        <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3
            id="conflict-saved-notes-heading"
            class="text-sm font-medium text-gray-800"
          >
            Notes
          </h3>
          <span
            class="text-xs text-gray-500 print:hidden"
            data-cy="conflict-saved-notes-lock"
          >
            You can change these until {{ lockDate }}. After that they are fixed
            with the rest of the check.
          </span>
          <div v-if="!uiState.editing" class="ml-auto print:hidden">
            <LlSecondaryButton
              size="small"
              prepend-icon="mdi-pencil-outline"
              data-cy="conflict-saved-notes-edit"
              @click="startEditing"
            >
              Edit notes
            </LlSecondaryButton>
          </div>
        </div>

        <div v-if="uiState.editing" class="mt-2 print:hidden">
          <ConflictNotesField
            id="conflict-saved-notes-input"
            v-model="uiState.draft"
            :disabled="recordStore.savingNotes"
          />
          <div class="mt-3 flex gap-3">
            <LlSecondaryButton
              color="primary"
              :loading="recordStore.savingNotes"
              :disabled="draftOverLimit"
              data-cy="conflict-saved-notes-save"
              @click="saveNotes"
            >
              Save notes
            </LlSecondaryButton>
            <LlSecondaryButton
              :disabled="recordStore.savingNotes"
              data-cy="conflict-saved-notes-cancel"
              @click="cancelEditing"
            >
              Cancel
            </LlSecondaryButton>
          </div>
        </div>

        <!-- The stored notes, which is also what prints while an edit is open. -->
        <div :class="uiState.editing ? 'print-only' : ''">
          <p
            v-if="record.notes"
            class="mt-1 whitespace-pre-line text-sm text-gray-800 [overflow-wrap:anywhere]"
          >
            {{ record.notes }}
          </p>
          <p v-else class="mt-1 text-sm italic text-gray-600">
            No notes were recorded.
          </p>
        </div>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import LlIcon from '@/components/LlIcon.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import ConflictNotesField from './ConflictNotesField.vue';
import ConflictPrintMasthead from './ConflictPrintMasthead.vue';
import ConflictResultList from './ConflictResultList.vue';
import { useConflictCheckRecordStore } from '../stores/useConflictCheckRecordStore';
import { CONFLICT_SCOPE_SENTENCE } from '../constants/conflictFieldConstants';
import { CONFLICT_NOTES_MAX_LENGTH } from '../constants/conflictRecordConstants';
import { formatRunAt, formatRunDate } from '../utils/conflictDateHelpers';
import type {
  ConflictPrintSheet,
  ConflictResultRow,
} from '../models/conflictTypes';

const emit = defineEmits<{
  back: [];
  retry: [];
  print: [];
  open: [row: ConflictResultRow];
  dirty: [dirty: boolean];
}>();

const recordStore = useConflictCheckRecordStore();
const { record } = storeToRefs(recordStore);
const { me } = storeToRefs(useMeStore());
const { handleError, handleSuccess } = useSnackbarHandler();

const uiState = reactive({ editing: false, draft: '' });
const resultList = ref<InstanceType<typeof ConflictResultList> | null>(null);

const runAt = computed(() => formatRunAt(record.value?.runAtUtc));
const lockDate = computed(() => formatRunDate(record.value?.notesLockAtUtc));
const scopeTail = computed(() =>
  record.value?.includedTimeEntries
    ? ', and time entry descriptions.'
    : '. Time entries were not searched.',
);
const draftOverLimit = computed(
  () => uiState.draft.length > CONFLICT_NOTES_MAX_LENGTH,
);

/**
 * Why these notes cannot change, in ERR-011/012's words. The server's refusal wins;
 * otherwise the lock date having passed tells a closed window from a non-author.
 */
const fixedNotesReason = computed(() => {
  const current = record.value;
  if (!current) return '';
  const locked = recordStore.notesRefusal
    ? recordStore.notesRefusal.reason === 'windowClosed'
    : new Date(current.notesLockAtUtc).getTime() <= Date.now();
  return locked
    ? `These notes locked on ${lockDate.value} and can no longer be changed.`
    : `Only ${current.runByName}, who ran this check, can change its notes.`;
});

const sheet = computed<ConflictPrintSheet>(() => ({
  firmName: me.value?.company?.name ?? '',
  runByName: record.value?.runByName ?? '',
  runAt: record.value?.runAtUtc ?? null,
  saved: true,
  queryText: record.value?.queryText ?? '',
  parties: record.value?.parties ?? [],
  includedTimeEntries: record.value?.includedTimeEntries ?? false,
  hiddenRowCount: 0,
}));

const dirty = computed(
  () => uiState.editing && uiState.draft !== (record.value?.notes ?? ''),
);

watch(dirty, (next) => emit('dirty', next));

// A different record, or one that stopped offering an edit, ends any edit in progress.
watch(
  () => [record.value?.id, record.value?.notesEditable] as const,
  ([id, editable], [previousId]) => {
    if (id !== previousId || !editable) cancelEditing();
  },
);

function startEditing(): void {
  uiState.draft = record.value?.notes ?? '';
  uiState.editing = true;
}

function cancelEditing(): void {
  uiState.editing = false;
  uiState.draft = '';
}

async function saveNotes(): Promise<void> {
  try {
    await recordStore.saveNotes(uiState.draft);
    uiState.editing = false;
    handleSuccess('Notes saved.');
  } catch (error) {
    // A 403 is answered on the record itself; anything else keeps the draft to retry.
    if (!recordStore.notesRefusal) handleError(error);
  }
}

/** The page calls this before printing — a collapsed panel prints as nothing. */
function expandPartials(): void {
  if (resultList.value) resultList.value.expandPartials();
}

defineExpose({ expandPartials });
</script>
